import { observer } from 'mobx-react-lite'
import { ArrowLeftIcon, ArrowUpIcon, ChevronDoubleUpIcon, ChevronLeftIcon, MapPinIcon } from '@heroicons/react/20/solid'
import { NavigationPresenter, type MenuItem } from './navigation.presenter'
import { useInjection } from '@core/providers/inversify-react.provider'
import { NavItemComponent } from './nav-item/nav-item.component'

export const NavigationComponent = observer(() => {
  const presenter: NavigationPresenter = useInjection(NavigationPresenter)
  const vm = presenter.viewModel
  const columns = vm.columns

  // Desktop shows the primary list and every open column side by side. A phone
  // isn't wide enough for that, so below md exactly one of them is visible at a
  // time — the deepest open column, or the primary list if none are open — with
  // a back button to step out one level. Both layouts read the same state; only
  // which nav block is display:none differs, so opening/closing a column behaves
  // identically either way. Which one that is comes pre-resolved from the
  // presenter (vm.primaryActiveOnMobile / vm.columnsActiveOnMobile).

  return (
    // No gap between columns and no items-start: the column's border-l is the only
    // divider — a gap left it floating detached from the previous column, and
    // items-start let shorter columns' border stop short of the taller ones.
    <div className="flex flex-1 min-h-0">
      <nav className={`${vm.primaryActiveOnMobile ? 'flex' : 'hidden'} md:flex flex-1 min-h-0 flex-col px-2 py-4`}>
        {/* Only this list scrolls — Up/Section below stays pinned, same as
            breadcrumbs above the whole nav (SidebarComponent). */}
        <ul className="scroll-thin flex flex-1 min-h-0 flex-col overflow-x-hidden overflow-y-auto p-1">
          {vm.menuItems.map((menuItem, i) => (
            <li key={menuItem.key + i}>
              <NavItemComponent menuItem={menuItem} expandLabel={vm.expandLabel} collapseLabel={vm.collapseLabel} />
            </li>
          ))}
        </ul>

        {vm.showUpSectionBar && (
          <div className="shrink-0 flex flex-col gap-y-1 pt-4">
            {/* Only while browsing: puts the menu back on the current route.
                The other way out is to navigate somewhere, which is what a user
                who is browsing rather than navigating is trying not to do. */}
            {vm.isBrowsingAwayFromRoute && (
              <button
                className="group flex items-center gap-x-2 rounded-md p-2 text-sm/6 font-semibold text-shell-accent hover:bg-shell-surface-muted"
                onClick={() => presenter.backToCurrent()}
              >
                <MapPinIcon className="size-4 shrink-0 text-shell-accent" aria-hidden="true" />
                {vm.backToCurrentLabel}
              </button>
            )}

            {/* Browse up — moves what the menu *lists* one level up the tree
                without going anywhere. Deliberately first and left-arrowed, so
                it does not read as a weaker Up: this one changes the view, Up
                changes the page. On a leaf route the menu has no children to
                show, and this is what gets the siblings back without leaving
                the page. */}
            {vm.browseUpTarget.enabled && (
              <button
                className="group flex items-center gap-x-2 rounded-md p-2 text-sm/6 font-semibold text-shell-ink-secondary hover:bg-shell-surface-muted hover:text-shell-accent"
                onClick={() => presenter.browseUp()}
                title={vm.browseUpTarget.target ?? undefined}
              >
                <ArrowLeftIcon
                  className="size-4 shrink-0 text-shell-ink-muted group-hover:text-shell-accent"
                  aria-hidden="true"
                />
                {vm.browseUpLabel}
              </button>
            )}

            {/* Up — one level, tree-structural parent */}
            {vm.currentSelectedUpTarget.enabled && (
              <button
                className="group flex items-center gap-x-2 rounded-md p-2 text-sm/6 font-semibold text-shell-ink-secondary hover:bg-shell-surface-muted hover:text-shell-accent"
                onClick={() => presenter.up()}
              >
                <ArrowUpIcon
                  className="size-4 shrink-0 text-shell-ink-muted group-hover:text-shell-accent"
                  aria-hidden="true"
                />
                {vm.upLabel}
              </button>
            )}

            {/* Section — configurable jump to a group/section root, can skip levels */}
            {vm.currentSelectedSectionTarget.enabled && (
              <button
                className="group flex items-center gap-x-2 rounded-md p-2 text-sm/6 font-semibold text-shell-ink-secondary hover:bg-shell-surface-muted hover:text-shell-accent"
                onClick={() => presenter.section()}
              >
                <ChevronDoubleUpIcon
                  className="size-4 shrink-0 text-shell-ink-muted group-hover:text-shell-accent"
                  aria-hidden="true"
                />
                {vm.sectionLabel}
              </button>
            )}
          </div>
        )}
      </nav>

      {/* Extra columns opened by columnView nodes — one per open node, cascading right
          on desktop; on mobile only the deepest one (the one currently "drilled into") is shown. */}
      {columns.map((columnItems: MenuItem[], columnIndex) => {
        return (
          <nav
            key={columnIndex}
            className={`${vm.columnsActiveOnMobile[columnIndex] ? 'flex' : 'hidden'} md:flex flex-1 min-h-0 flex-col border-shell-border px-2 py-4 md:border-l`}
          >
            {/* Drill-down back step — only meaningful when columns stack instead of
                sitting side by side, so it's mobile-only. */}
            <button
              type="button"
              onClick={() => presenter.closeLastColumn()}
              className="mb-2 flex shrink-0 items-center gap-x-1 self-start rounded-md p-2 text-sm/6 font-semibold text-shell-ink-secondary hover:bg-shell-surface-muted hover:text-shell-accent md:hidden"
            >
              <ChevronLeftIcon className="size-4 shrink-0 text-shell-ink-muted" aria-hidden="true" />
              {vm.columnLabels[columnIndex]}
            </button>
            <ul className="scroll-thin flex flex-1 min-h-0 flex-col overflow-x-hidden overflow-y-auto p-1">
              {columnItems.map((menuItem, i) => (
                <li key={menuItem.key + i}>
                  <NavItemComponent menuItem={menuItem} expandLabel={vm.expandLabel} collapseLabel={vm.collapseLabel} />
                </li>
              ))}
            </ul>
          </nav>
        )
      })}
    </div>
  )
})
